/**
 * What to offer under a "not found" answer, so a refusal is a next step rather
 * than a dead end (2026-09-27).
 *
 * The first questions kept for review showed that the misses were rarely badly
 * worded questions. Several readers simply wanted to READ a book («dime
 * <libro>», «quiero leer <libro>»). The chat cannot serve that, but the library
 * can, or the book is not in it yet, and the old notice («Intenta
 * reformular…») said neither. (Examples here and in the smoke are made up in
 * the readers' patterns: kept questions are deleted after 30 days, as «Acerca
 * de» promises, so their text never goes into the repository.)
 *
 * A book counts only on a clear signal: a chapter number («Hechos 2»), a
 * reading word before it («leer», «el libro de», «carta a los»), or the book
 * alone («muéstrame génesis»). Never a bare name inside a question. Most book
 * names are also people or common words («¿Quién era Judas?», «los hebreos»),
 * and a wrong suggestion reads worse than the plain notice. «Not in the
 * library yet» needs the strongest signals (a chapter or a reading word),
 * because the book alone is usually a person («Judas»).
 *
 * Pure: no fetch, no DOM. The rules are smoke-tested in refusal-help.smoke.ts.
 */
import type { Language } from "../strings";
import { POPULAR_QUESTION_SETS } from "./popular-questions";

interface BookName {
  slug: string;
  /** Display name when the library does not supply one. */
  name: string;
  /** Normalised forms (see normalizeQuery): accent-free, lowercase, "1 juan". */
  aliases: string[];
  /**
   * Short forms («Gn», «Am», «1 Cor»). Many are also words («dan», «sal», «am»),
   * so they count only right before a chapter and verse («Am. 3:7»).
   */
  abbreviations?: string[];
  /**
   * A different work in the library shares this book's name. The library's
   * «Santiago» is the apocryphal Protevangelium, not the letter of James, so
   * «Santiago 2:10» must not open it as if it were.
   */
  libraryHomonym?: string;
}

/**
 * Canonical books, then the extra books the library holds or readers name.
 * Readers also write the Hebrew names the teachings use («Yirmeyahu 18»,
 * «MatitYahu 23», «2 Shemuel»), so those count too: the forms below were
 * checked against the cleaned transcripts (2026-10-01), and the names Koresh's
 * own scripture reader gives the books (losdelcamino.net/las-escrituras, read
 * 2026-10-03: Shemot, Shoftim, Melakim, El HaRomim, LeKeif, Hitgalut…), the
 * same list rag/bible-reference.ts reads. Left out on purpose:
 * Yaakov and Yahudah, which almost always mean the patriarch and the tribe,
 * and Yahoshua/Yehoshua, one letter from the Messiah's name.
 */
const BOOKS: BookName[] = [
  { slug: "genesis", name: "Génesis", aliases: ["genesis", "bereshit", "bereshith"], abbreviations: ["gen", "gn"] },
  { slug: "exodo", name: "Éxodo", aliases: ["exodo", "shemot", "shemoth"], abbreviations: ["ex", "exo"] },
  { slug: "levitico", name: "Levítico", aliases: ["levitico", "vayikra", "vaikra"], abbreviations: ["lev", "lv"] },
  { slug: "numeros", name: "Números", aliases: ["numeros", "bamidbar", "bemidbar"], abbreviations: ["num", "nm"] },
  { slug: "deuteronomio", name: "Deuteronomio", aliases: ["deuteronomio", "devarim", "debarim"], abbreviations: ["deut", "dt"] },
  { slug: "josue", name: "Josué", aliases: ["josue"], abbreviations: ["jos"] },
  { slug: "jueces", name: "Jueces", aliases: ["jueces", "shoftim", "shofetim"], abbreviations: ["jue", "jc"] },
  { slug: "rut", name: "Rut", aliases: ["rut", "ruth"] },
  { slug: "1samuel", name: "1 Samuel", aliases: ["1 samuel", "1 shemuel", "1 shmuel"], abbreviations: ["1 sam", "1 s"] },
  { slug: "2samuel", name: "2 Samuel", aliases: ["2 samuel", "2 shemuel", "2 shmuel"], abbreviations: ["2 sam", "2 s"] },
  { slug: "1reyes", name: "1 Reyes", aliases: ["1 reyes", "1 melakim", "1 melajim"], abbreviations: ["1 re", "1 r"] },
  { slug: "2reyes", name: "2 Reyes", aliases: ["2 reyes", "2 melakim", "2 melajim"], abbreviations: ["2 re", "2 r"] },
  { slug: "1cronicas", name: "1 Crónicas", aliases: ["1 cronicas", "1 divrei hayamim", "1 dibre hayamim"], abbreviations: ["1 cro", "1 cr"] },
  { slug: "2cronicas", name: "2 Crónicas", aliases: ["2 cronicas", "2 divrei hayamim", "2 dibre hayamim"], abbreviations: ["2 cro", "2 cr"] },
  { slug: "esdras", name: "Esdras", aliases: ["esdras", "ezra"], abbreviations: ["esd"] },
  { slug: "nehemias", name: "Nehemías", aliases: ["nehemias", "nehemyah", "nejemyah"], abbreviations: ["neh"] },
  { slug: "ester", name: "Ester", aliases: ["ester", "esther"], abbreviations: ["est"] },
  { slug: "job", name: "Job", aliases: ["job", "iyov", "yov"] },
  { slug: "salmos", name: "Salmos", aliases: ["salmos", "salmo", "tehilim", "tehillim"], abbreviations: ["sal"] },
  { slug: "proverbios", name: "Proverbios", aliases: ["proverbios", "mishlei", "mishle"], abbreviations: ["pr", "prov"] },
  { slug: "eclesiastes", name: "Eclesiastés", aliases: ["eclesiastes", "kohelet", "qohelet"], abbreviations: ["ecl"] },
  { slug: "cantares", name: "Cantares", aliases: ["cantares", "cantar de los cantares", "shir hashirim"], abbreviations: ["cant", "cnt"] },
  { slug: "isaias", name: "Isaías", aliases: ["isaias", "yeshayahu", "yishayahu"], abbreviations: ["is", "isa"] },
  { slug: "jeremias", name: "Jeremías", aliases: ["jeremias", "yirmeyahu", "yirmyahu", "yirmiyahu"], abbreviations: ["jer"] },
  { slug: "lamentaciones", name: "Lamentaciones", aliases: ["lamentaciones", "eikah", "eijah", "eichah"], abbreviations: ["lam"] },
  { slug: "ezequiel", name: "Ezequiel", aliases: ["ezequiel", "yejezkel", "yejezqel", "yehezkel"], abbreviations: ["eze", "ez"] },
  { slug: "daniel", name: "Daniel", aliases: ["daniel"], abbreviations: ["dn", "dan"] },
  { slug: "oseas", name: "Oseas", aliases: ["oseas", "hoshea"], abbreviations: ["os"] },
  { slug: "joel", name: "Joel", aliases: ["joel", "yoel"], abbreviations: ["jl"] },
  { slug: "amos", name: "Amós", aliases: ["amos"], abbreviations: ["am"] },
  { slug: "abdias", name: "Abdías", aliases: ["abdias", "ovadyah", "obadyah"], abbreviations: ["abd"] },
  { slug: "jonas", name: "Jonás", aliases: ["jonas", "yonah"], abbreviations: ["jon"] },
  { slug: "miqueas", name: "Miqueas", aliases: ["miqueas", "mikah", "mijah"], abbreviations: ["miq"] },
  { slug: "nahum", name: "Nahúm", aliases: ["nahum", "najum"], abbreviations: ["nah"] },
  { slug: "habacuc", name: "Habacuc", aliases: ["habacuc", "javakuk", "habakuk"], abbreviations: ["hab"] },
  { slug: "sofonias", name: "Sofonías", aliases: ["sofonias", "tzefanyah", "tsefanyah"], abbreviations: ["sof"] },
  { slug: "hageo", name: "Hageo", aliases: ["hageo", "jagai", "hagai"], abbreviations: ["hag"] },
  { slug: "zacarias", name: "Zacarías", aliases: ["zacarias", "zejaryah", "zekaryah", "zejaryahu", "zekaryahu"], abbreviations: ["zac"] },
  { slug: "malaquias", name: "Malaquías", aliases: ["malaquias", "malaji", "malakias", "malaki"], abbreviations: ["mal"] },
  { slug: "mateo", name: "Mateo", aliases: ["mateo", "matityahu", "mattityahu"], abbreviations: ["mt", "mat"] },
  { slug: "marcos", name: "Marcos", aliases: ["marcos"], abbreviations: ["mc", "mr"] },
  { slug: "lucas", name: "Lucas", aliases: ["lucas", "lukas"], abbreviations: ["lc", "luc"] },
  { slug: "juan", name: "Juan", aliases: ["juan", "yohanan", "yojanan", "yahujanan"], abbreviations: ["jn"] },
  // The apostrophe in «Ma'asei» becomes a space in normalizeQuery.
  { slug: "hechos", name: "Hechos", aliases: ["hechos", "hechos de los apostoles", "maasei hamalakim", "ma asei hamalakim"], abbreviations: ["hch"] },
  { slug: "romanos", name: "Romanos", aliases: ["romanos", "haromim", "romim"], abbreviations: ["rom", "ro"] },
  { slug: "1corintios", name: "1 Corintios", aliases: ["1 corintios", "1 hakorintim", "1 korintim"], abbreviations: ["1 cor", "1 co"] },
  { slug: "2corintios", name: "2 Corintios", aliases: ["2 corintios", "2 hakorintim", "2 korintim"], abbreviations: ["2 cor", "2 co"] },
  { slug: "galatas", name: "Gálatas", aliases: ["galatas", "hagalatim", "galatim"], abbreviations: ["gal", "ga"] },
  { slug: "efesios", name: "Efesios", aliases: ["efesios", "haefesim", "efesim"], abbreviations: ["ef"] },
  { slug: "filipenses", name: "Filipenses", aliases: ["filipenses", "hapilipim", "pilipim"], abbreviations: ["fil"] },
  { slug: "colosenses", name: "Colosenses", aliases: ["colosenses", "hakolasim", "kolasim"], abbreviations: ["col"] },
  { slug: "1tesalonicenses", name: "1 Tesalonicenses", aliases: ["1 tesalonicenses", "1 hatesalonikim", "1 tesalonikim"], abbreviations: ["1 tes", "1 ts"] },
  { slug: "2tesalonicenses", name: "2 Tesalonicenses", aliases: ["2 tesalonicenses", "2 hatesalonikim", "2 tesalonikim"], abbreviations: ["2 tes", "2 ts"] },
  { slug: "1timoteo", name: "1 Timoteo", aliases: ["1 timoteo", "1 timotios", "1 el timotios"], abbreviations: ["1 tim", "1 ti"] },
  { slug: "2timoteo", name: "2 Timoteo", aliases: ["2 timoteo", "2 timotios", "2 el timotios"], abbreviations: ["2 tim", "2 ti"] },
  // Timoteo with no number («Timoteo 3:16», «la carta a Timotios»): either
  // letter, and neither is in the library. «tinoteo» is the m/n slip seen in a
  // kept question.
  { slug: "timoteo", name: "Timoteo", aliases: ["timoteo", "timotios", "el timotios", "tinoteo"] },
  { slug: "tito", name: "Tito", aliases: ["tito", "titos"], abbreviations: ["tit"] },
  { slug: "filemon", name: "Filemón", aliases: ["filemon", "pileimon"], abbreviations: ["flm"] },
  { slug: "hebreos", name: "Hebreos", aliases: ["hebreos", "haibrim", "haivrim"], abbreviations: ["heb"] },
  {
    slug: "santiago-carta",
    name: "La carta de Santiago",
    aliases: ["santiago", "carta de santiago", "epistola de santiago"],
    abbreviations: ["stg"],
    libraryHomonym: "santiago",
  },
  { slug: "1pedro", name: "1 Pedro", aliases: ["1 pedro", "1 lekeif", "1 keif", "1 kefa"], abbreviations: ["1 pe", "1 ped"] },
  { slug: "2pedro", name: "2 Pedro", aliases: ["2 pedro", "2 lekeif", "2 keif", "2 kefa"], abbreviations: ["2 pe", "2 ped"] },
  { slug: "1juan", name: "1 Juan", aliases: ["1 juan", "1 yohanan", "1 yojanan", "1 yahujanan", "1 leyahujanan"], abbreviations: ["1 jn"] },
  { slug: "2juan", name: "2 Juan", aliases: ["2 juan", "2 yohanan", "2 yojanan", "2 yahujanan", "2 leyahujanan"], abbreviations: ["2 jn"] },
  { slug: "3juan", name: "3 Juan", aliases: ["3 juan", "3 yohanan", "3 yojanan", "3 yahujanan", "3 leyahujanan"], abbreviations: ["3 jn"] },
  { slug: "judas", name: "Judas", aliases: ["judas"], abbreviations: ["jud"] },
  { slug: "apocalipsis", name: "Apocalipsis", aliases: ["apocalipsis", "revelacion", "hitgalut"], abbreviations: ["ap", "apoc"] },
  // Beyond the canon: the library's own extra books, and a few readers ask for.
  { slug: "tobias", name: "Tobías", aliases: ["tobias", "tobit", "tobiyah"] },
  { slug: "judith", name: "Judit", aliases: ["judith", "judit", "yahudith", "yahudit", "yehudit"] },
  { slug: "eclesiastico", name: "Eclesiástico", aliases: ["eclesiastico", "sirac", "sirach", "ben sira"] },
  { slug: "baruc", name: "Baruc", aliases: ["baruc", "baruk"] },
  { slug: "nikodemos", name: "Nicodemo", aliases: ["nikodemos", "nicodemos", "nicodemo"] },
  { slug: "bernabe", name: "Bernabé", aliases: ["bernabe", "barnabe", "barnavi"] },
  { slug: "jubileos", name: "Jubileos", aliases: ["jubileos", "yobelim"] },
  { slug: "yashar", name: "Yashar", aliases: ["yashar", "jaser", "jasher"] },
  {
    slug: "t12patriarcas",
    name: "12 Patriarcas",
    aliases: ["12 patriarcas", "doce patriarcas"],
  },
  { slug: "librosecretojuan", name: "Libro Secreto de Juan", aliases: ["libro secreto de juan", "apocrifo de juan"] },
  { slug: "silvano", name: "Silvano", aliases: ["silvano"] },
  { slug: "origenhijosyahweh", name: "El Origen de los Hijos de Yahwéh", aliases: ["origen de los hijos de yahweh"] },
  { slug: "felipe", name: "Felipe", aliases: ["felipe"] },
  { slug: "2esdras", name: "Apocalipsis de Esdras", aliases: ["2 esdras", "4 esdras", "2 ezra", "4 ezra", "apocalipsis de esdras"] },
  // Not the canonical Esdras: the apocryphal book Koresh's reader lists as «1 Ezra».
  { slug: "1esdras", name: "1 Esdras", aliases: ["1 esdras", "1 ezra"] },
  { slug: "arimatea", name: "José de Arimatea", aliases: ["jose de arimatea", "arimatea"] },
  {
    slug: "santiago",
    name: "Buenas Nuevas de Santiago",
    aliases: ["buenas nuevas de santiago", "protoevangelio de santiago", "protevangelio de santiago", "evangelio de santiago"],
  },
  { slug: "tecla", name: "Tecla", aliases: ["tecla", "hechos de pablo y tecla"] },
  { slug: "clemente", name: "Carta de Clemente", aliases: ["clemente", "carta de clemente", "1 clemente"] },
  { slug: "tomas", name: "Tomás", aliases: ["tomas"] },
  { slug: "enoc", name: "Enoc", aliases: ["enoc", "henoc"] },
  { slug: "1macabeos", name: "1 Macabeos", aliases: ["1 macabeos"] },
  { slug: "2macabeos", name: "2 Macabeos", aliases: ["2 macabeos"] },
  { slug: "sabiduria", name: "Sabiduría", aliases: ["libro de la sabiduria"] },
];

const BOOK_BY_SLUG = new Map(BOOKS.map((b) => [b.slug, b]));

/** Longest alias first, so «libro secreto de juan» wins over «juan» and
 *  «hechos de pablo y tecla» over «hechos» at the same position. */
const ALIASES: Array<{ alias: string; slug: string; abbreviation: boolean }> = BOOKS.flatMap((b) => [
  ...b.aliases.map((alias) => ({ alias, slug: b.slug, abbreviation: false })),
  ...(b.abbreviations ?? []).map((alias) => ({ alias, slug: b.slug, abbreviation: true })),
]).sort((a, b) => b.alias.length - a.alias.length);

// No lookbehind: older iOS Safari (before 16.4) rejects it, and a regex that
// fails to compile would take the whole chat down with this module.
const ALIAS_RE = new RegExp(
  `(^|[^a-z0-9])(${ALIASES.map((a) => a.alias.replace(/ /g, "\\s+")).join("|")})(?![a-z])`,
);
const BOOK_BY_ALIAS = new Map(ALIASES.map((a) => [a.alias, a]));

/** Stand-in for «capítulo N de», kept whole so the ordinal rules below cannot
 *  read «capitulo 2 de juan» as the book 2 Juan. */
const CHAPTER_MARK = "chq";

/**
 * Accent-free, lowercase, punctuation to spaces, ordinals to digits:
 * «Primera de Corintios 13» → "1 corintios 13", «1Juan» → "1 juan".
 */
export function normalizeQuery(text: string): string {
  let s = text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  s = s.replace(/[^a-z0-9:\s]/g, " ").replace(/([a-z])(\d)/g, "$1 $2").replace(/\s+/g, " ");
  s = s.replace(/\b(?:capitulo|cap)\s+(\d{1,3})(?:\s+|$)(?:de\s+|del\s+)?(?:(?:el\s+)?libro\s+de\s+)?/g, `${CHAPTER_MARK}$1 `);
  s = s
    .replace(/\b(?:primera|primero|primer|1ra|1era|1ro|1er|1a|1o)\b/g, "1")
    .replace(/\b(?:segunda|segundo|2da|2do|2a|2o)\b/g, "2")
    .replace(/\b(?:tercera|tercero|3ra|3era|3ro|3er|3a|3o)\b/g, "3");
  s = s.replace(
    /\b(iii|ii|i)\s+(?=(?:samuel|shemuel|shmuel|reyes|melakim|melajim|cronicas|corintios|hakorintim|korintim|tesalonicenses|hatesalonikim|tesalonikim|timoteo|timotios|pedro|lekeif|keif|kefa|juan|yohanan|yojanan|yahujanan|leyahujanan|esdras|ezra|macabeos)\b)/g,
    (_m, roman: string) => `${roman.length} `,
  );
  // «1 de corintios», «1 carta de pedro», «1ra epístola de san juan» → "1 corintios"
  s = s.replace(/\b([1234])\s+(?:(?:carta|epistola)\s+)?(?:de\s+)?(?:san\s+)?(?=[a-z])/g, "$1 ");
  s = s.replace(/\b([1234])(?=[a-z])/g, "$1 ");
  return s.replace(/\s+/g, " ").trim();
}

/** Words that, right before a book, mean "I want to read it". */
const READING_CUE_RE =
  /\b(?:leer|lee|leeme|leerme|leerlo|abrir|abre|abreme|abrime|libro|carta a los|carta a|epistola a los|evangelio)\s+(?:(?:de|del|el|la|los|las|a|san)\s+)*$/;

/** Words that may surround a book the reader only wants to see («muéstrame génesis»). */
const FILLER = new Set([
  "dime", "decime", "quiero", "quisiera", "leer", "lee", "leeme", "ver", "abrir", "abre", "muestrame",
  "mostrar", "mostrame", "ensename", "el", "la", "los", "las", "un", "una", "libro", "de", "del", "sobre",
  "acerca", "a", "al", "y", "en", "por", "favor", "porfa", "hola", "me", "puedes", "podrias", "completo",
  "entero", "todo", "toda", "resumen", "que", "dice", "info", "informacion", "capitulos", "biblia",
  "lectura", "estudio", "estudiar", "buenas", "nuevas", "carta", "evangelio", "capitulo",
]);

export interface BookIntent {
  slug: string;
  /** The chapter named («Hechos 2», «capítulo 3 de Génesis»), if any. */
  chapter: number | null;
  /** A reading word before the book, or nothing but the book and filler. */
  reading: boolean;
  /** Only a chapter or a reading word counts; the book alone does not. */
  strong: boolean;
}

/**
 * Every book a question clearly asks to read or look up, in order. All of
 * them, not just the first named: «¿Lo que dice Enoc sobre los vigilantes viene
 * de Génesis 6?» names Enoc in passing, and Génesis 6 is the help.
 */
export function detectBookIntents(query: string): BookIntent[] {
  const text = normalizeQuery(query);
  const re = new RegExp(ALIAS_RE.source, "g");
  const intents: BookIntent[] = [];
  for (let match = re.exec(text); match !== null; match = re.exec(text)) {
    const intent = intentAt(text, match);
    if (intent !== null) intents.push(intent);
  }
  return intents;
}

/** The first book a question clearly asks to read or look up, or null. */
export function detectBookIntent(query: string): BookIntent | null {
  return detectBookIntents(query)[0] ?? null;
}

function intentAt(text: string, match: RegExpExecArray): BookIntent | null {
  if (match[2] === undefined) return null;
  const found = BOOK_BY_ALIAS.get(match[2].replace(/\s+/g, " "));
  if (found === undefined) return null;
  const slug = found.slug;

  const start = match.index + (match[1]?.length ?? 0);
  const before = text.slice(0, start);
  const after = text.slice(start + match[2].length);
  const following = new RegExp(`^\\s+(?:${CHAPTER_MARK})?(\\d{1,3})\\b`).exec(after);
  if (found.abbreviation && !/^\s+\d{1,3}:\d/.test(after)) return null;
  const marked = new RegExp(`${CHAPTER_MARK}(\\d{1,3})\\s+$`).exec(before);
  const raw = following?.[1] ?? marked?.[1];
  const chapter = raw !== undefined && Number(raw) <= 150 ? Number(raw) : null;

  const cue = READING_CUE_RE.test(before);
  const rest = `${before} ${after}`
    .replace(new RegExp(`${CHAPTER_MARK}\\d+`, "g"), " ")
    .replace(/\d+(?::\d+)?/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 0);
  const alone = rest.every((w) => FILLER.has(w));

  if (chapter === null && !cue && !alone) return null;
  return { slug, chapter, reading: cue || alone, strong: chapter !== null || cue };
}

/**
 * The answer service's exact refusal sentences (rag/answer-refusal.ts).
 * Normally a refusal is that sentence alone, with no sources. But the service's
 * second look sometimes writes its reasoning first («Las fuentes tratan el alma…,
 * pero ninguna menciona a X») and ends with the sentence; its citations then
 * made it look like an answer, with unrelated sources and no card. All 14 such
 * answers recorded on 2026-10-03 were refusals in substance
 * (docs/reranker-trial-2026-10-03.md). The server folds the ones that open with
 * a negation; the rest stream before the sentence arrives, so the chat decides.
 */
const REFUSAL_SENTENCES = [
  "No encontramos esta enseñanza en el corpus de Koresh.",
  "We did not find this teaching in Koresh's corpus.",
];

/** Whether a finished answer refuses: the refusal sentence, alone or after reasoning. */
export function answerRefuses(text: string): boolean {
  return REFUSAL_SENTENCES.some((sentence) => text.includes(sentence));
}

export interface LibraryBook {
  name: string;
  chapters: readonly number[];
}
export type LibraryIndex = ReadonlyMap<string, LibraryBook>;

/** The reader manifest (corpus-data/manifest.json) reduced to what is needed here. */
export function toLibraryIndex(manifest: unknown): LibraryIndex | null {
  const books = (manifest as { books?: unknown } | null)?.books;
  if (!Array.isArray(books)) return null;
  const index = new Map<string, LibraryBook>();
  for (const b of books as Array<Record<string, unknown>>) {
    if (typeof b.slug !== "string" || typeof b.name !== "string" || !Array.isArray(b.chapters)) continue;
    const chapters = (b.chapters as Array<Record<string, unknown>>)
      .map((c) => c.chapter)
      .filter((c): c is number => typeof c === "number")
      .sort((x, y) => x - y);
    index.set(b.slug, { name: b.name, chapters });
  }
  return index.size > 0 ? index : null;
}

/*
 * Links always open a chapter (`open`), never the bare book: the library has
 * no page for a book alone (/corpus/:book shows the whole index), while a
 * chapter opens with the book's chapter list beside it.
 */
export type RefusalHelp =
  /** The book is in the library: open it, or ask about a chapter. */
  | {
      kind: "book";
      slug: string;
      name: string;
      /** The chapter the reader named, if any. */
      chapter: number | null;
      /** The chapter to open: the one named, else the book's first. */
      open: number;
      reading: boolean;
      questions: string[];
    }
  /** The book is in the library but that chapter is not. */
  | { kind: "chapter_missing"; slug: string; name: string; chapter: number; open: number }
  /** Not in the library; `homonym` is a different library work of the same name. */
  | { kind: "missing"; name: string; homonym: { slug: string; name: string; open: number } | null }
  /** A task for a general assistant (looksLikeTask): say what the tool does. */
  | { kind: "task" }
  | { kind: "general" };

/**
 * The library link under an ANSWERED question that was really a request to
 * read a book («muéstrame génesis», «quiero leer génesis», a bare «Génesis 3»).
 * The second look answers these by stitching together whatever passages of the
 * book were found (seen live 2026-09-27: Génesis 29, 30, 37 and 38 for a
 * two-word request), which leaves a reader who wanted the book with no way in. Only a
 * reading request qualifies. A question that names a book («¿Qué significa
 * Génesis 3:15?») already has its answer and its source links. Null when the
 * book or chapter is not in the library.
 */
export function planReadingLink(
  query: string,
  library: LibraryIndex | null,
): { slug: string; name: string; chapter: number | null; open: number } | null {
  if (library === null) return null;
  const intent = detectBookIntents(query).find((i) => i.reading && library.has(i.slug));
  if (intent === undefined) return null;
  const held = library.get(intent.slug);
  if (held === undefined) return null;
  if (intent.chapter !== null && !held.chapters.includes(intent.chapter)) return null;
  const open = intent.chapter ?? firstChapter(held);
  return open === null ? null : { slug: intent.slug, name: held.name, chapter: intent.chapter, open };
}

/** A book's first chapter, skipping a chapter 0 (a prologue) when there is a 1. */
function firstChapter(book: LibraryBook): number | null {
  return book.chapters.find((c) => c >= 1) ?? book.chapters[0] ?? null;
}

const QUESTION_LIMIT = 3;

/**
 * A task for a general assistant rather than a question about the teaching: a
 * long prompt, a numbered list of inputs, or explicit rules or a role. One
 * kept on 2026-09-27 asked for a 12-name meaning chain to be composed into a
 * message under numbered rules. The refusal is right (Arco y Flecha does not
 * make new interpretations), and «prueba con otras palabras» is the wrong
 * advice: it needs to say what the tool does.
 */
export function looksLikeTask(query: string): boolean {
  if (query.length >= 400) return true;
  const listLines = query.split("\n").filter((line) => /^\s*(?:\d{1,2}[.)]|[-*•])\s+\S/.test(line)).length;
  if (listLines >= 3) return true;
  const text = normalizeQuery(query);
  return /\b(?:reglas|instrucciones|formato)\s*:/i.test(query) || /\b(?:actua como|eres un|eres una)\b/.test(text);
}

/** «Santiago (apócrifos)» → «Santiago»; «El Libro de Yashar» → «el Libro de Yashar». */
export function inlineBookName(name: string): string {
  return name.replace(/\s*\([^)]*\)\s*$/, "").replace(/^(El|La|Los|Las) /, (article) => article.toLowerCase());
}

/**
 * The help to show under a refusal. `library` null (not loaded, or failed):
 * nothing can be said about a book, so a book question gets the general help.
 * `ask` phrases the chapter question in the reader's language.
 */
export function planRefusalHelp(
  query: string,
  library: LibraryIndex | null,
  language: Language,
  ask: (passage: string) => string,
): RefusalHelp {
  if (looksLikeTask(query)) return { kind: "task" };
  if (library === null) return { kind: "general" };
  // A book the library holds is the most useful help; a book it lacks only
  // on a strong signal (a chapter or a reading word).
  const intents = detectBookIntents(query);
  const intent = intents.find((i) => library.has(i.slug)) ?? intents.find((i) => i.strong) ?? null;
  if (intent === null) return { kind: "general" };
  const book = BOOK_BY_SLUG.get(intent.slug);
  const held = library.get(intent.slug);

  if (held === undefined) {
    if (!intent.strong) return { kind: "general" };
    const homonymSlug = book?.libraryHomonym;
    const homonym = homonymSlug !== undefined ? library.get(homonymSlug) : undefined;
    const homonymOpen = homonym !== undefined ? firstChapter(homonym) : null;
    return {
      kind: "missing",
      name: book?.name ?? intent.slug,
      homonym: homonymSlug !== undefined && homonym !== undefined && homonymOpen !== null
        ? { slug: homonymSlug, name: homonym.name, open: homonymOpen }
        : null,
    };
  }

  const first = firstChapter(held);
  if (first === null) return { kind: "general" };
  const inline = inlineBookName(held.name);
  if (intent.chapter !== null && !held.chapters.includes(intent.chapter)) {
    return { kind: "chapter_missing", slug: intent.slug, name: held.name, chapter: intent.chapter, open: first };
  }
  const chapter = intent.chapter ?? first;
  const questions: string[] = [ask(`${inline} ${chapter}`)];
  // Curated questions (known to be answerable): this chapter's first, then one
  // per other curated chapter of the book, for variety.
  const sets = POPULAR_QUESTION_SETS.filter((s) => s.book === intent.slug);
  const own = sets.find((s) => s.chapter === chapter);
  const curated = [
    ...(own?.questions ?? []),
    ...(intent.chapter === null ? sets.filter((s) => s !== own).map((s) => s.questions[0]) : []),
  ];
  for (const q of curated) {
    if (questions.length >= QUESTION_LIMIT) break;
    if (q !== undefined && !questions.includes(q[language])) questions.push(q[language]);
  }
  return {
    kind: "book",
    slug: intent.slug,
    name: held.name,
    chapter: intent.chapter,
    open: chapter,
    reading: intent.reading,
    questions,
  };
}
